import { useEffect, useRef, useState } from "react";
import type { ResearchFeed } from "@kmitl/shared";
import ResearchPanel from "./ResearchPanel";

// Same convention as App.tsx: an absolute base when one is configured
// (production uses https://kmitl-api.nonarkara.org), otherwise SAME-ORIGIN so
// the Vite dev proxy forwards /api to the API. The old dev fallback of
// "http://localhost:8795" pointed the browser at the API directly, which
// fails in dev — the API binds IPv4 only while `localhost` resolves to ::1
// first, and every fetch in this board rejected with "TypeError: Failed to
// fetch" before a single panel could load.
const API = import.meta.env.VITE_API_BASE_URL ?? "";

// ── DB types (fetched live) ────────────────────────────────────────────────────
interface AqRow   { bucket: string; avg: string; min: string; max: string; n: string }
interface WxSeries { bucket: string; temperature_avg?: string; humidity_avg?: string }
interface NewsDay  { day: string; articles: string; th: string; en: string }
interface FeedRow  { feed_key: string; snapshots: string; latest_ts: string; avg_age: string; dominant_tier: string }
interface RecentItem { url_hash: string; ts: string; title: string; source: string; language: string; url: string }
interface AqData   { metric: string; hours: number; rows: AqRow[]; count: number }
interface WxData   { hours: number; series: WxSeries[]; count: number }
interface NewsData  { days: number; byDay: NewsDay[]; recent: RecentItem[]; totalStored: number }
interface FeedsData { days: number; feeds: FeedRow[]; count: number }

interface BuildingLoad {
  id: string; name: string; zone: string;
  currentKw: number; peakKw: number; loadPct: number;
  kwhThisHour: number; tariffTHBThisHour: number;
}
interface EnergySnapshot {
  totalKwDemand: number; gridImportKw: number;
  solarKwGeneration: number; solarOffsetPct: number;
  carbonKgPerHour: number; kwhToday: number; costToday: number;
  powerFactor: number; mainTransformerLoadPct: number;
  peakDemandKwToday: number; peakDemandThreshold: number;
  buildings: BuildingLoad[];
}
interface EnergyFeed { features: EnergySnapshot[]; meta: { fallbackTier: string } }

interface Initiative {
  id: string; name: string; status: string;
  progressPct: number; owner: string; deadline: string; describe: string;
}
interface Peer {
  name: string; country: string;
  qsWorldRank: number; theWorldRank: number;
  studentsTotal: number; internationalPct: number; researchOutput: string;
}
interface ExecFeed {
  features: Array<{
    initiatives: Initiative[];
    peers: Peer[];
    smartCampus: {
      annualEnergyMwh: number; annualEnergyCostTHBM: number;
      solarInstalledKwp: number; carbonTonnesPerYear: number;
      estimatedSavingsTHBMLow: number; estimatedSavingsTHBMHigh: number;
    };
  }>;
}

// ── Static reference data (sourced from KMITL Data Source Bible, June 2026) ──
const PROFILE = [
  { label: "STUDENTS",        value: "27,090",   sub: "QS 20,945 / THE 27,090" },
  { label: "ACADEMIC STAFF",  value: "1,211",    sub: "2023 · 24.8 students/staff" },
  { label: "BUILDINGS",       value: "52",       sub: "4 zones (A–D) · 1,345 rai" },
  { label: "PROGRAMMES",      value: "216",      sub: "102 bachelor · 76 master · 35 PhD" },
  { label: "INT'L STUDENTS",  value: "1,099",    sub: "3% of total · QS 2026" },
  { label: "INT'L STAFF",     value: "18%",      sub: "38.3 international outlook" },
  { label: "MoU PARTNERS",    value: "200+",     sub: "50+ collaborating countries" },
  { label: "WiFi ACCESS PTS", value: "3,200",    sub: "campus-wide coverage" },
];

const RANKINGS = [
  { system: "QS 2026",          global: "1201–1400",  thailand: "#10",  score: null,  highlight: null,    note: "Acad. Reputation 13.5 · Employment 10.7" },
  { system: "THE 2026",         global: "1501+",      thailand: "~#12", score: "26.1", highlight: "INDUSTRY INCOME 74.5", note: "THE's highest KMITL sub-score" },
  { system: "Webometrics Jan 26",global: "#1,328",    thailand: "#10",  score: null,  highlight: null,    note: "Up from #1,368 · Jul 2024" },
  { system: "Nature Index 2025", global: "—",          thailand: "#7",  score: "0.38", highlight: null,   note: "Share (natural sciences journals)" },
  { system: "THE Impact 2025",   global: "301–600",   thailand: "~#7", score: "66–71", highlight: null,   note: "SDG 7 · SDG 9 · SDG 14 (strongest)" },
  { system: "THE Eng. 2026",     global: "1001–1250", thailand: "—",    score: null,  highlight: null,    note: "Core discipline ranking" },
];


const SDG_SCORES = [
  { sdg: "SDG 7",  label: "Affordable & Clean Energy",        lo: 57.8, hi: 62.2, color: "#F5C800" },
  { sdg: "SDG 9",  label: "Industry, Innovation & Infrastr.", lo: 60.3, hi: 72.9, color: "#00B4FF" },
  { sdg: "SDG 14", label: "Life Below Water",                  lo: 56.8, hi: 69.9, color: "#18C2A8" },
  { sdg: "SDG 4",  label: "Quality Education",                 lo: 0,    hi: 0,    color: "#8B5CF6", note: "data pending" },
  { sdg: "SDG 17", label: "Partnerships for the Goals",        lo: 0,    hi: 0,    color: "#F5A623", note: "data pending" },
];

const BKK_AIRPORT = [
  { label: "FY2025 PASSENGERS", value: "62.66M", delta: "+4.44%",  color: "var(--good)" },
  { label: "AIRCRAFT MOVEMENTS", value: "368,735", delta: "+6.36%", color: "var(--good)" },
  { label: "CARGO (TONNES)",    value: "1.47M",   delta: "+10.68%", color: "var(--good)" },
  { label: "HOURLY CAPACITY",   value: "94 flt/h", delta: "↑ from 68 (Nov 2024)", color: "var(--data)" },
  { label: "DAILY MOVEMENTS",   value: "800–1,000", delta: "3rd runway live",     color: "var(--data)" },
];

// ── Sub-components ─────────────────────────────────────────────────────────────
function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div className="section-eyebrow mono">{children}</div>;
}

function RefBadge() {
  return <span className="ref-badge mono" title="Static reference data — sourced from KMITL Data Source Bible, June 2026">REF</span>;
}

/** Campus profile — one lead figure plus a field of supporting values.
 *
 *  The eight cells used to render as eight identical tiles: every value at
 *  26px/700 in a 4-column grid, so nothing in the block was more important
 *  than anything else and the eye had no entry point. The first item becomes
 *  the lead — full row, 44px — and the rest drop to 20px field values. */
function StatGrid({ items }: { items: typeof PROFILE }) {
  const [lead, ...rest] = items;
  return (
    <div className="stat-grid">
      <div className="stat-cell stat-cell--lead">
        <span className="stat-value mono">{lead.value}</span>
        <div className="stat-lead__meta">
          <span className="stat-label mono">{lead.label}</span>
          <span className="stat-sub mono">{lead.sub}</span>
        </div>
      </div>
      {rest.map((s) => (
        <div key={s.label} className="stat-cell">
          <span className="stat-value mono">{s.value}</span>
          <span className="stat-label mono">{s.label}</span>
          <span className="stat-sub mono">{s.sub}</span>
        </div>
      ))}
    </div>
  );
}

function RankingsTable({ rows }: { rows: typeof RANKINGS }) {
  return (
    <table className="analytics-table">
      <thead>
        <tr>
          <th>SYSTEM</th>
          <th>GLOBAL</th>
          <th>THAILAND</th>
          <th>SCORE</th>
          <th>NOTE</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.system}>
            <td className="mono" style={{ color: "var(--ink)" }}>{r.system}</td>
            <td className="mono">{r.global}</td>
            <td className="mono" style={{ color: "var(--data)" }}>{r.thailand}</td>
            <td className="mono">
              {r.score ?? "—"}
              {r.highlight && (
                <span className="ranking-highlight mono"> · {r.highlight}</span>
              )}
            </td>
            <td className="mono" style={{ color: "var(--ink-low)", fontSize: "10px" }}>{r.note}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SdgBars({ scores }: { scores: typeof SDG_SCORES }) {
  return (
    <div className="sdg-bars">
      {scores.map((s) => (
        <div key={s.sdg} className="sdg-row">
          <span className="sdg-label mono" style={{ color: s.color }}>{s.sdg}</span>
          <span className="sdg-name mono">{s.label}</span>
          <div className="sdg-bar-track">
            {s.lo > 0 && (
              <div
                className="sdg-bar-fill"
                style={{ width: `${s.hi}%`, background: s.color, opacity: 0.8 }}
                title={`${s.lo}–${s.hi} / 100`}
              />
            )}
          </div>
          <span className="sdg-score mono" style={{ color: s.lo > 0 ? s.color : "var(--ink-low)" }}>
            {s.lo > 0 ? `${s.lo}–${s.hi}` : (s.note ?? "—")}
          </span>
        </div>
      ))}
    </div>
  );
}

function AirportStats({ items }: { items: typeof BKK_AIRPORT }) {
  return (
    <div className="airport-stats">
      {items.map((a) => (
        <div key={a.label} className="airport-stat">
          <span className="airport-val mono" style={{ color: a.color }}>{a.value}</span>
          <span className="airport-delta mono" style={{ color: a.color }}>{a.delta}</span>
          <span className="airport-label mono">{a.label}</span>
        </div>
      ))}
    </div>
  );
}

function Sparkline({ data, color, label, unit }: { data: number[]; color: string; label: string; unit: string }) {
  if (data.length < 2) return <div className="spark-empty">no data yet</div>;
  const W = 260; const H = 56;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (W - 2) + 1;
    const y = H - 4 - ((v - min) / range) * (H - 8);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const path = `M${pts.join("L")}`;
  const area = `M${pts[0]}L${pts.join("L")}L${(W - 1).toFixed(1)},${H}L1,${H}Z`;
  const latest = data[data.length - 1].toFixed(1);
  return (
    <div className="spark-wrap">
      <div className="spark-meta">
        <span className="spark-label mono">{label}</span>
        <span className="spark-val" style={{ color }}>{latest}<span className="spark-unit">{unit}</span></span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: "block" }}>
        <defs>
          <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#grad-${label})`} />
        <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
        <circle cx={pts[pts.length - 1].split(",")[0]} cy={pts[pts.length - 1].split(",")[1]} r="3" fill={color} />
      </svg>
      <div className="spark-range mono">
        <span>{min.toFixed(1)}</span><span>{max.toFixed(1)}</span>
      </div>
    </div>
  );
}

function NewsBarChart({ days }: { days: NewsDay[] }) {
  if (!days.length) return <div className="spark-empty">no data yet</div>;
  const max = Math.max(...days.map((d) => Number(d.articles)), 1);
  return (
    <div className="news-bars">
      {days.map((d) => {
        const pct = (Number(d.articles) / max) * 100;
        const label = new Date(d.day).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
        return (
          <div key={d.day} className="news-bar-col" title={`${d.articles} articles`}>
            <span className="news-bar-val mono">{d.articles}</span>
            <div className="news-bar-track">
              <div className="news-bar-fill" style={{ height: `${pct}%`, background: "var(--data)" }} />
            </div>
            <span className="news-bar-label mono">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

const TIER_COLOR: Record<string, string> = {
  live: "var(--good)", cache: "var(--warn)", scenario: "var(--ink-mid)", unavailable: "var(--bad)",
};

function FeedTable({ feeds }: { feeds: FeedRow[] }) {
  if (!feeds.length) return <div className="spark-empty">no data yet</div>;
  return (
    <table className="analytics-table">
      <thead><tr><th>FEED</th><th>SNAPS</th><th>AVG AGE</th><th>TIER</th><th>LATEST</th></tr></thead>
      <tbody>
        {feeds.map((f) => (
          <tr key={f.feed_key}>
            <td className="mono">{f.feed_key}</td>
            <td className="mono">{f.snapshots}</td>
            <td className="mono">{f.avg_age}m</td>
            <td style={{ color: TIER_COLOR[f.dominant_tier] ?? "inherit" }} className="mono">{f.dominant_tier}</td>
            <td className="mono" style={{ color: "var(--ink-low)", fontSize: "11px" }}>
              {new Date(f.latest_ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EnergyBuildingBars({ buildings }: { buildings: BuildingLoad[] }) {
  const sorted = [...buildings].sort((a, b) => b.loadPct - a.loadPct);
  return (
    <div className="field-bars" style={{ gap: 7 }}>
      {sorted.map((b) => {
        const color = b.loadPct > 85 ? "var(--bad)" : b.loadPct > 65 ? "var(--warn)" : "var(--good)";
        return (
          <div key={b.id} className="field-bar-row" title={`${b.currentKw.toFixed(0)} kW · ${b.kwhThisHour.toFixed(1)} kWh/h`}>
            <span className="field-bar-label mono" style={{ fontSize: 10, width: 130, flexShrink: 0 }}>{b.name}</span>
            <div className="field-bar-track">
              <div className="field-bar-fill" style={{ width: `${b.loadPct}%`, background: color }} />
            </div>
            <span className="field-bar-val mono" style={{ color, fontSize: 10 }}>{b.loadPct.toFixed(0)}%</span>
            <span className="field-bar-cites mono" style={{ fontSize: 10, color: "var(--ink-low)" }}>
              {b.currentKw.toFixed(0)} kW
            </span>
          </div>
        );
      })}
    </div>
  );
}

function EnergySummaryGrid({ snap }: { snap: EnergySnapshot }) {
  const cells = [
    { label: "TOTAL DEMAND",   value: `${snap.totalKwDemand.toFixed(0)} kW`,  color: "var(--data)" },
    { label: "SOLAR OUTPUT",   value: `${snap.solarKwGeneration.toFixed(0)} kW`, color: "var(--good)" },
    { label: "SOLAR OFFSET",   value: `${snap.solarOffsetPct.toFixed(1)}%`,   color: "var(--good)" },
    { label: "kWh TODAY",      value: snap.kwhToday.toFixed(0),                color: "var(--ink)" },
    { label: "COST TODAY",     value: `฿${snap.costToday.toFixed(0)}`,          color: "var(--warn)" },
    { label: "CO₂ NOW",        value: `${snap.carbonKgPerHour.toFixed(1)} kg/h`, color: "var(--ink-mid)" },
    { label: "POWER FACTOR",   value: snap.powerFactor.toFixed(2),             color: snap.powerFactor < 0.85 ? "var(--bad)" : "var(--good)" },
    { label: "TRANSFORMER",    value: `${snap.mainTransformerLoadPct.toFixed(0)}%`, color: snap.mainTransformerLoadPct > 80 ? "var(--bad)" : "var(--data)" },
  ];
  return (
    <div className="stat-grid">
      {cells.map((c) => (
        <div key={c.label} className="stat-cell">
          <span className="stat-value mono" style={{ color: c.color }}>{c.value}</span>
          <span className="stat-label mono">{c.label}</span>
        </div>
      ))}
    </div>
  );
}

const STATUS_COLOR: Record<string, string> = {
  "on-track": "var(--good)", "at-risk": "var(--warn)",
  "delayed": "var(--bad)", "completed": "var(--data)", "planning": "var(--ink-mid)",
};

function InitiativesBars({ items }: { items: Initiative[] }) {
  return (
    <div className="field-bars" style={{ gap: 10 }}>
      {items.map((item) => {
        const color = STATUS_COLOR[item.status] ?? "var(--data)";
        const deadline = item.deadline ? new Date(item.deadline).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }) : "—";
        return (
          <div key={item.id} title={item.describe}>
            <div className="field-bar-row">
              <span className="field-bar-label mono" style={{ fontSize: 10, width: 180, flexShrink: 0, lineHeight: 1.3 }}>{item.name}</span>
              <div className="field-bar-track">
                <div className="field-bar-fill" style={{ width: `${item.progressPct}%`, background: color }} />
              </div>
              <span className="field-bar-val mono" style={{ color, fontSize: 10 }}>{item.progressPct}%</span>
              <span className="field-bar-cites mono" style={{ fontSize: 9, color: "var(--ink-low)" }}>
                {item.status} · {deadline}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PeerTable({ peers }: { peers: Peer[] }) {
  const KMITL_QS = 1300;
  const all = [
    { name: "KMITL", country: "TH", qsWorldRank: KMITL_QS, theWorldRank: 1501, studentsTotal: 27090, internationalPct: 4.1, researchOutput: "High" },
    ...peers,
  ].sort((a, b) => (a.qsWorldRank || 9999) - (b.qsWorldRank || 9999));

  return (
    <table className="analytics-table">
      <thead>
        <tr>
          <th>UNIVERSITY</th>
          <th>COUNTRY</th>
          <th>QS WORLD</th>
          <th>THE WORLD</th>
          <th>STUDENTS</th>
          <th>INT'L %</th>
          <th>RESEARCH</th>
        </tr>
      </thead>
      <tbody>
        {all.map((p) => {
          const isKmitl = p.name === "KMITL";
          return (
            <tr key={p.name} style={isKmitl ? { background: "rgba(232,0,45,0.06)" } : undefined}>
              <td className="mono" style={{ color: isKmitl ? "var(--bad)" : "var(--ink)", fontWeight: isKmitl ? 700 : 400 }}>{p.name}</td>
              <td className="mono" style={{ color: "var(--ink-mid)" }}>{p.country}</td>
              <td className="mono" style={{ color: isKmitl ? "var(--data)" : "inherit" }}>
                {p.qsWorldRank ? `#${p.qsWorldRank.toLocaleString()}` : "—"}
              </td>
              <td className="mono">{p.theWorldRank ? `#${p.theWorldRank.toLocaleString()}` : "—"}</td>
              <td className="mono">{p.studentsTotal ? p.studentsTotal.toLocaleString() : "—"}</td>
              <td className="mono">{p.internationalPct ? `${p.internationalPct}%` : "—"}</td>
              <td className="mono" style={{ color: p.researchOutput === "Very High" ? "var(--good)" : "var(--data)" }}>
                {p.researchOutput ?? "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Main board ─────────────────────────────────────────────────────────────────
interface Props { open: boolean; onClose: () => void }

export function AnalyticsBoard({ open, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [aq, setAq]       = useState<AqData | null>(null);
  const [wx, setWx]       = useState<WxData | null>(null);
  const [news, setNews]   = useState<NewsData | null>(null);
  const [feeds, setFeeds] = useState<FeedsData | null>(null);
  const [energy, setEnergy] = useState<EnergySnapshot | null>(null);
  const [exec, setExec]   = useState<ExecFeed["features"][0] | null>(null);
  // Research is fetched on its own so a failure there cannot take down the
  // whole board — Promise.all below would reject and blank every panel.
  const [research, setResearch] = useState<ResearchFeed | null>(null);
  const [researchMeta, setResearchMeta] = useState<{
    ageMinutes?: number | null;
    fallbackTier?: "live" | "unavailable" | "scenario" | "cache" | "database" | "reference";
  } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    ref.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/analytics/aq?hours=48`).then((r) => r.json() as Promise<AqData>),
      fetch(`${API}/api/analytics/weather?hours=48`).then((r) => r.json() as Promise<WxData>),
      fetch(`${API}/api/analytics/news?days=7`).then((r) => r.json() as Promise<NewsData>),
      fetch(`${API}/api/analytics/feeds?days=7`).then((r) => r.json() as Promise<FeedsData>),
      fetch(`${API}/api/campus/energy`).then((r) => r.json() as Promise<EnergyFeed>),
      fetch(`${API}/api/executive`).then((r) => r.json() as Promise<ExecFeed>),
    ])
      .then(([aqD, wxD, newsD, feedsD, energyD, execD]) => {
        setAq(aqD); setWx(wxD); setNews(newsD); setFeeds(feedsD);
        if (energyD.features?.[0]) setEnergy(energyD.features[0]);
        if (execD.features?.[0]) setExec(execD.features[0]);
      })
      .catch((err) => console.error("[analytics-board]", err))
      .finally(() => setLoading(false));
      fetch(`${API}/api/research`)
        .then((r) => r.json() as Promise<{ features: ResearchFeed[]; meta: typeof researchMeta }>)
        .then((d) => { setResearch(d.features[0] ?? null); setResearchMeta(d.meta); })
        .catch(() => { setResearch(null); setResearchMeta({ fallbackTier: "unavailable" }); });
  }, [open]);

  if (!open) return null;

  const pm25Values = (aq?.rows  ?? []).map((r) => parseFloat(r.avg));
  const tempValues = (wx?.series ?? []).map((s) => parseFloat(s.temperature_avg ?? "0")).filter(Boolean);
  const humidValues = (wx?.series ?? []).map((s) => parseFloat(s.humidity_avg   ?? "0")).filter(Boolean);

  return (
    <div
      className="analytics-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Analytics Board"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="analytics-panel" ref={ref} tabIndex={-1}>
        <header className="analytics-head">
          <div>
            {/* No inline colour: the header is an --ink plate that inverts between
                themes, so the eyebrow has to follow it. An inline var(--accent)
                here forced the stylesheet to fight it with !important, and the
                result was accent-on-cream at 2.62:1 in dark theme. CSS owns it. */}
            <span className="eyebrow mono">KMITL CONTROL TOWER</span>
            <h2>ANALYTICS BOARD</h2>
          </div>
          <button className="close-btn mono" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="analytics-body">

          {/* ── 1. Campus Profile ── */}
          <section className="analytics-section">
            <Eyebrow>KMITL CAMPUS PROFILE <RefBadge /></Eyebrow>
            <StatGrid items={PROFILE} />
          </section>

          {/* ── 2. Global Rankings ── */}
          <section className="analytics-section">
            <Eyebrow>
              GLOBAL RANKINGS 2025–26
              <RefBadge />
              <span className="section-stat mono" style={{ color: "var(--bad)" }}>
                THE INDUSTRY INCOME 74.5 — strongest score across all systems
              </span>
            </Eyebrow>
            <RankingsTable rows={RANKINGS} />
          </section>

          {/* ── 3. Research Output — live from OpenAlex ──
              Replaces a hardcoded Scopus block (15,533 publications etc.) whose
              "publications by field" chart carried cites: 0 for five of six
              fields. Scopus is licensed and has no open API; OpenAlex is CC0. */}
          <ResearchPanel
            feed={research}
            loading={loading && !research}
            ageMinutes={researchMeta?.ageMinutes}
            fallbackTier={researchMeta?.fallbackTier}
          />

          {/* ── 4. SDG Impact Rankings ── */}
          <section className="analytics-section">
            <Eyebrow>THE IMPACT RANKINGS 2025 — SDG SCORES <RefBadge /></Eyebrow>
            <p className="analytics-note mono">Overall: 66.3–70.9 · Global 301–600 · Thailand ~#7</p>
            <SdgBars scores={SDG_SCORES} />
          </section>

          {/* ── 5. Suvarnabhumi Airport (5 km) ── */}
          <section className="analytics-section">
            <Eyebrow>
              SUVARNABHUMI AIRPORT (5–7 km from campus) <RefBadge />
              <span className="section-stat mono">Relevant to IAAI aviation programmes</span>
            </Eyebrow>
            <AirportStats items={BKK_AIRPORT} />
            <p className="analytics-note mono">
              3rd runway opened Nov 2024 · AOT FY2025 · ARL connection: 1 stop, 15 THB, ~5 min
            </p>
          </section>

          {/* ── 6. Environment (live DB) ── */}
          <section className="analytics-section">
            <Eyebrow>ENVIRONMENT · LAST 48 HOURS</Eyebrow>
            {loading ? (
              <div className="analytics-loading mono">LOADING…</div>
            ) : (
              <div className="spark-row">
                <Sparkline data={pm25Values} color="var(--warn)" label="PM2.5" unit=" µg/m³" />
                <Sparkline data={tempValues} color="var(--data)" label="TEMPERATURE" unit="°C" />
                <Sparkline data={humidValues} color="#7aa2ff" label="HUMIDITY" unit="%" />
              </div>
            )}
          </section>

          {/* ── 7. News ── */}
          <section className="analytics-section">
            <Eyebrow>
              NEWS INGEST · LAST 7 DAYS
              {news && <span className="section-stat mono">{news.totalStored.toLocaleString()} ITEMS IN DB</span>}
            </Eyebrow>
            {loading ? (
              <div className="analytics-loading mono">LOADING…</div>
            ) : (
              <>
                <NewsBarChart days={news?.byDay ?? []} />
                {news?.recent && news.recent.length > 0 && (
                  <div className="news-recent">
                    {news.recent.slice(0, 5).map((item) => (
                      <a key={item.url_hash} href={item.url} target="_blank" rel="noopener noreferrer" className="news-recent-item">
                        <span className="news-source mono">{item.source}</span>
                        <span className="news-title">{item.title}</span>
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>

          {/* ── 8. Feed Health ── */}
          <section className="analytics-section">
            <Eyebrow>FEED HEALTH · LAST 7 DAYS</Eyebrow>
            {loading ? (
              <div className="analytics-loading mono">LOADING…</div>
            ) : (
              <FeedTable feeds={feeds?.feeds ?? []} />
            )}
          </section>

          {/* ── 9. Campus Energy (live) ── */}
          <section className="analytics-section">
            <Eyebrow>
              CAMPUS ENERGY · LIVE
              {energy && (
                <span className="section-stat mono" style={{ color: "var(--good)" }}>
                  {energy.solarOffsetPct.toFixed(1)}% SOLAR OFFSET · ฿{energy.costToday.toFixed(0)} TODAY
                </span>
              )}
            </Eyebrow>
            {loading ? (
              <div className="analytics-loading mono">LOADING…</div>
            ) : energy ? (
              <>
                <EnergySummaryGrid snap={energy} />
                <div style={{ marginTop: 14 }}>
                  <span className="section-eyebrow mono" style={{ fontSize: "9px", marginBottom: 6, display: "block" }}>
                    BUILDING LOAD — {energy.buildings.length} METERED BUILDINGS
                  </span>
                  <EnergyBuildingBars buildings={energy.buildings} />
                </div>
                <p className="analytics-note mono" style={{ marginTop: 8 }}>
                  Scenario tier · BEMS integration pending · PEA TOU tariff: ฿4.13/kWh peak · ฿2.64/kWh off-peak
                </p>
              </>
            ) : (
              <div className="analytics-loading mono">ENERGY FEED UNAVAILABLE</div>
            )}
          </section>

          {/* ── 10. Strategic Initiatives ── */}
          {exec?.initiatives && exec.initiatives.length > 0 && (
            <section className="analytics-section">
              <Eyebrow>STRATEGIC INITIATIVES <RefBadge /></Eyebrow>
              <InitiativesBars items={exec.initiatives} />
            </section>
          )}

          {/* ── 11. Peer Comparison ── */}
          {exec?.peers && exec.peers.length > 0 && (
            <section className="analytics-section">
              <Eyebrow>PEER COMPARISON — ASEAN ENGINEERING UNIVERSITIES <RefBadge /></Eyebrow>
              <PeerTable peers={exec.peers} />
              <p className="analytics-note mono" style={{ marginTop: 8 }}>
                KMITL highlighted · QS 2026 · THE 2026 · sorted by QS rank
              </p>
            </section>
          )}

        </div>
      </div>
    </div>
  );
}
